import React, { useEffect, useRef, useCallback, useState } from 'react';
import './ShareModal.css';
import {
  popoverContainerStyle,
  popoverHeaderStyle
} from './PopoverStyles';
import Button from './Button';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shared Share Modal Component
 * Issue #312: Social media sharing for posts, marketplace objects, and course reviews
 *
 * Uses Web Share API when available, falls back to social media buttons
 * Supports WhatsApp, Telegram, Signal, and Copy to Clipboard
 * Full keyboard accessibility (WCAG 2.1 compliant)
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close callback
 * @param {string} shareUrl - The URL to share
 * @param {string} shareText - The text message to share
 * @param {string} shareTitle - The title for native share dialog
 * @param {string} contentType - 'post' | 'object' | 'course' - Type of content being shared
 */
const ShareModal = ({
  isOpen,
  onClose,
  shareUrl,
  shareText,
  shareTitle,
  contentType = 'post'
}) => {
  const { t } = useLanguage();
  const modalRef = useRef(null);
  const firstButtonRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState(null);

  // Check if Web Share API is available
  const canUseNativeShare = typeof navigator !== 'undefined' && navigator.share;

  // Focus first button when modal opens
  useEffect(() => {
    if (isOpen && firstButtonRef.current) {
      setTimeout(() => {
        firstButtonRef.current?.focus();
      }, 10);
    }
    // Reset states when modal opens
    if (isOpen) {
      setCopied(false);
      setShareError(null);
    }
  }, [isOpen]);

  // Handle keyboard navigation and focus trap
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }

    // Focus trap - Tab and Shift+Tab
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button:not([disabled])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // Try native share first if available
  const handleNativeShare = async () => {
    if (!canUseNativeShare) return;

    try {
      await navigator.share({
        title: shareTitle,
        text: shareText,
        url: shareUrl
      });
      onClose();
    } catch (err) {
      // User cancelled or error occurred
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
        setShareError(t('share.errorSharing'));
      }
    }
  };

  // Share via WhatsApp
  const handleWhatsAppShare = () => {
    const message = `${shareText}\n${shareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    onClose();
  };

  // Share via Telegram
  const handleTelegramShare = () => {
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(telegramUrl, '_blank', 'noopener,noreferrer');
    onClose();
  };

  // Share via Instagram (copies message and opens Instagram Direct)
  const handleInstagramShare = async () => {
    const textToCopy = `${shareText}\n${shareUrl}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Copy failed:', err);
    }
    // Open Instagram Direct
    window.open('https://www.instagram.com/direct/inbox/', '_blank', 'noopener,noreferrer');
    onClose();
  };


  // Copy link to clipboard with fallback for older browsers
  const handleCopyLink = async () => {
    const textToCopy = `${shareText}\n${shareUrl}`;

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers or restricted contexts
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!successful) {
          throw new Error('execCommand copy failed');
        }
      }
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Copy failed:', err);
      setShareError(t('share.errorCopying'));
    }
  };

  if (!isOpen) return null;

  // Get title based on content type
  const getModalTitle = () => {
    switch (contentType) {
      case 'post':
        return t('share.sharePost');
      case 'object':
        return t('share.shareObject');
      case 'course':
        return t('share.shareCourse');
      default:
        return t('posts.share');
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={popoverContainerStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        tabIndex={-1}
        className="share-modal-container"
      >
        <h2 id="share-modal-title" style={popoverHeaderStyle}>{getModalTitle()}</h2>

        {shareError && (
          <p className="share-error" role="alert">{shareError}</p>
        )}

        <div className="share-buttons-grid" role="group" aria-label={t('share.channels')}>
          {/* WhatsApp */}
          <button
            ref={firstButtonRef}
            type="button"
            className="share-button share-button-whatsapp"
            onClick={handleWhatsAppShare}
            aria-label={t('share.shareViaWhatsApp')}
          >
            <span className="share-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </span>
            <span className="share-label">WhatsApp</span>
          </button>

          {/* Telegram */}
          <button
            type="button"
            className="share-button share-button-telegram"
            onClick={handleTelegramShare}
            aria-label={t('share.shareViaTelegram')}
          >
            <span className="share-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
            </span>
            <span className="share-label">Telegram</span>
          </button>

          {/* Instagram */}
          <button
            type="button"
            className="share-button share-button-instagram"
            onClick={handleInstagramShare}
            aria-label={t('share.shareViaInstagram')}
          >
            <span className="share-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </span>
            <span className="share-label">Instagram</span>
          </button>

          {/* More Options (Native Share) - for Signal and other apps */}
          {canUseNativeShare && (
            <button
              type="button"
              className="share-button share-button-more"
              onClick={handleNativeShare}
              aria-label={t('share.moreOptions')}
            >
              <span className="share-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="2"/>
                  <circle cx="12" cy="5" r="2"/>
                  <circle cx="12" cy="19" r="2"/>
                </svg>
              </span>
              <span className="share-label">{t('share.moreOptions')}</span>
            </button>
          )}

          {/* Copy Link */}
          <button
            type="button"
            className="share-button share-button-copy"
            onClick={handleCopyLink}
            aria-label={copied ? t('share.copied') : t('share.copyLink')}
          >
            <span className="share-icon" aria-hidden="true">
              {copied ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="currentColor" strokeWidth="2"/>
                </svg>
              )}
            </span>
            <span className="share-label">{copied ? t('share.copied') : t('share.copyLink')}</span>
          </button>
        </div>

        {/* Close button */}
        <Button
          variant="secondary"
          fullWidth
          style={{ marginTop: '8px' }}
          onClick={onClose}
          aria-label={t('close')}
        >
          {t('close')}
        </Button>
      </div>
    </div>
  );
};

export default ShareModal;
